/* eslint-disable import/prefer-default-export */
import {
  apiDownloadDocument,
  apiActionDocument,
  apiRenameDocument,
  apiEditTypeDocument,
  apiSetDatesDocument,
} from 'api/document-management';
import {
  apiVerifyProductFiles,
  apiDeleteProductFiles,
  apiProductMoveFileFolderV2,
  apiCreateSharedProductDocumentToken,
} from 'api';
import { getDateWithFormat, getMomentDate } from 'util/date';
import { capitalizeFirstLetter } from 'util/icwUtility';
import useDownloadFile from 'hooks/useDownloadFile';
import { useCreate } from 'context/ResourceProvider/hooks/useCreate';
import { ComplianceMappings } from 'mappers/ComplianceDataMapper';
import { DocumentListMapper } from 'mappers/DocumentListMapper';
import get from 'lodash/get';
import useCurrentUser from 'hooks/useCurrentUser';
import { openShareDocumentEmailEditor } from 'util/email';

const ACTION_MESSAGE_MAPPING = {
  delete: 'Document Deleted',
  archive: 'Archive Succeeded',
  unarchive: 'Unarchive Succeeded',
  restore: 'Document Restored',
};
const DATE_FORMAT = 'YYYY-MM-DD';

export const useFunctionBarActions = (
  setNotification,
  reloadCurrentTabData,
  reloadSelectedKeys
) => {
  const me = useCurrentUser();
  const companyName = me?.companyMaps?.[0]?.company?.company_name || '';
  const [download] = useDownloadFile();
  const { mutate: setDataDocument } = useCreate({
    resource: 'document-list/document-list-index',
    meta: {
      method: 'PUT',
    },
  });

  const handleDownloadDocuments = (tRows) => async (cb) => {
    try {
      const res = await apiDownloadDocument({
        documentListIds: tRows.map((obj) => obj.id),
      });
      if (res?.data) {
        const fileName = res?.headers['content-disposition']?.split('filename=')[1];
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        download(fileName, url);
        if (reloadSelectedKeys) reloadSelectedKeys();
        if (cb) cb();
        setNotification({
          type: 'success',
          message: 'Download Succeeded',
        });
      } else {
        if (cb) cb();
        setNotification({
          type: 'error',
          message: 'Download Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Download Failed',
      });
    }
  };
  const handleActionDocuments = (tRows, key) => async (cb) => {
    try {
      const res = await apiActionDocument(key, {
        documentListIds: tRows.map((obj) => obj.id),
      });
      if (cb) cb();
      if ([200, 201].includes(res?.data?.code)) {
        if (reloadCurrentTabData)
          reloadCurrentTabData(['archive', 'unarchive'].includes(key) ? 1 : '');
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message: ACTION_MESSAGE_MAPPING[key] || `${capitalizeFirstLetter(key)} Succeeded`,
        });
      } else {
        setNotification({
          type: 'error',
          message: ACTION_MESSAGE_MAPPING[key] || `${capitalizeFirstLetter(key)} Failed`,
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: ACTION_MESSAGE_MAPPING[key] || `${capitalizeFirstLetter(key)} Failed`,
      });
    }
  };
  const handleRenameDocuments = (tRows) => async (val, cb) => {
    try {
      const res = await apiRenameDocument(tRows?.[0]?.id, {
        name: val,
      });
      if (cb) cb();
      if ([200, 201].includes(res?.data?.code)) {
        if (reloadCurrentTabData) reloadCurrentTabData();
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message: 'Rename Succeeded',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Rename Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Rename Failed',
      });
    }
  };
  const handleEditTypeDocuments = (tRows) => async (val, cb) => {
    try {
      const tPayload = {
        documentListIds: tRows.map((obj) => obj.id),
        tagIds: [val],
      };
      const res = await apiEditTypeDocument(tPayload);
      if (cb) cb();
      if ([200, 201].includes(res?.data?.code)) {
        if (reloadCurrentTabData) reloadCurrentTabData();
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message: 'Edit Succeeded',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Edit Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Edit Failed',
      });
    }
  };
  const handleSetDatesDocuments = (tRows) => async (values, cb) => {
    try {
      const tPayload = {
        documentListIds: tRows.map((obj) => obj.id),
        issueDate: getDateWithFormat(values.issueDate, DATE_FORMAT) || null,
        expiryDate: getDateWithFormat(values.expiryDate, DATE_FORMAT) || null,
        reviewDate: getDateWithFormat(values.reviewDate, DATE_FORMAT) || null,
        reminderType: values.emailReminder || null,
        note: values.note || null,
      };
      if (values?.emailReminder === 'onCustomDate') {
        tPayload.reminderDate = getDateWithFormat(values.customDate, DATE_FORMAT) || null;
      }
      const res = await apiSetDatesDocument(tPayload);
      if (cb) cb();
      if ([200, 201].includes(res?.data?.code)) {
        if (reloadCurrentTabData) reloadCurrentTabData();
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message: 'Set Dates Succeeded',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Set Dates Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Set Dates Failed',
      });
    }
  };
  const handleShare = (tRows, productId, message) => async (val, cb) => {
    try {
      const today = getMomentDate('', true);
      const tPayload = {
        ids: tRows.map((obj) => obj.originalId),
        expiryDate: today.add(val, 'days').format(DATE_FORMAT),
      };
      const res = await apiCreateSharedProductDocumentToken(productId, tPayload);
      const token = res?.data?.data?.token;
      if (cb) cb();
      if (!!token) {
        const host = window.location.protocol + '//' + window.location.host;
        const encryptToken = encodeURIComponent(token);
        const tLink = `${host}/documents/shared/${encryptToken}`;
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message,
        });
        return tLink;
      } else {
        setNotification({
          type: 'error',
          message: 'Shared Failed',
        });
        return '';
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Shared Failed',
      });
      return '';
    }
  };
  const handleShareDocuments = (tRows, productId) => async (val, cb) => {
    const tLink = await handleShare(tRows, productId, 'Link copied')(val, cb);
    if (tLink) {
      navigator.clipboard.writeText(tLink);
    }
  };
  const handleShareDocumentsEmail = (tRows, productId) => async (val, cb) => {
    const tLink = await handleShare(tRows, productId, 'Email shared')(val, cb);
    if (tLink) {
      openShareDocumentEmailEditor({
        companyName,
        link: tLink,
      });
    }
  };
  const handleVerifyDocument = async (productId, fileId, status, notes, cb) => {
    try {
      const res = await apiVerifyProductFiles(productId, fileId, { status, verifyNotes: notes });
      if (!!res?.data?.success) {
        if (reloadCurrentTabData) reloadCurrentTabData();
        setNotification({
          type: 'success',
          message: 'File verified',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Verify Failed',
        });
      }
      if (cb) cb();
    } catch (err) {
      setNotification({
        type: 'error',
        message: 'Verify Failed',
      });
      if (cb) cb();
    }
  };
  const handleDeleteDocuments = (tRows, productId) => async (cb) => {
    try {
      const res = await apiDeleteProductFiles(productId, {
        ids: tRows.map((obj) => obj.originalId),
      });
      if ([200, 201].includes(res?.data?.code)) {
        if (reloadCurrentTabData) reloadCurrentTabData(1);
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message: 'Remove Succeeded',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Remove Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Remove Failed',
      });
    }
  };
  const handleMoveDocuments = (tRows, productId) => async (tFolderId, cb) => {
    try {
      const payload = {
        fileIds: tRows.map((obj) => obj.originalId),
        data: {
          parentId: tFolderId || 0,
        },
      };
      const res = await apiProductMoveFileFolderV2(productId, payload);
      if ([200, 201].includes(res?.data?.code)) {
        if (reloadCurrentTabData) reloadCurrentTabData(1);
        if (reloadSelectedKeys) reloadSelectedKeys();
        setNotification({
          type: 'success',
          message: 'Moved Succeeded',
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Move Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Move Failed',
      });
    }
  };

  const handleSetDataDocuments = (tRows) => async (val, form, cb) => {
    try {
      const { complianceData } = val;
      const payload = {
        documentLists: tRows.map((obj) => ({
          id: obj.id,
          fileName: obj.name,
        })),
        complianceData: ComplianceMappings.mapComplianceDataToMutate(complianceData),
      };
      await setDataDocument(payload);
      setNotification({
        type: 'success',
        message: 'Set Data Succeeded',
      });

      if (reloadCurrentTabData) reloadCurrentTabData();
      if (reloadSelectedKeys) reloadSelectedKeys();
      cb();
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Set Data Failed',
      });
      cb();
    }
  };

  const handleUpdateDocument = async (
    values,
    _form,
    cb,
    onNotificationSuccess,
    onNotificationError
  ) => {
    try {
      const { complianceData, ...rest } = values;
      const payload = DocumentListMapper.updateDTO(rest);
      const mappedComplianceData = ComplianceMappings.mapComplianceDataToMutate(complianceData);
      const res = await setDataDocument({
        ...payload,
        complianceData: {
          ...mappedComplianceData,
        },
      });
      if (onNotificationSuccess) {
        onNotificationSuccess(get(data, 'data', {}));
      } else {
        setNotification({
          type: 'success',
          message: 'Set Document Succeeded',
        });
      }

      if (reloadCurrentTabData) reloadCurrentTabData();
      cb &&
        cb({
          status: 'success',
          data: get(res, 'data', {}),
        });
    } catch (error) {
      if (onNotificationError) {
        onNotificationError(error);
      } else {
        setNotification({
          type: 'error',
          message: 'Update Document Failed',
        });
      }
      cb &&
        cb({
          status: 'error',
          error,
        });
    }
  };
  return {
    handleDownloadDocuments,
    handleActionDocuments,
    handleRenameDocuments,
    handleEditTypeDocuments,
    handleSetDatesDocuments,
    handleShareDocuments,
    handleVerifyDocument,
    handleDeleteDocuments,
    handleMoveDocuments,
    handleSetDataDocuments,
    handleUpdateDocument,
    handleShareDocumentsEmail,
  };
};
